// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { FormValidator } from '../../../utils/form';
import { DEFAULT_COUNTRIES, getLocalizedCountry } from '../../../modules/local-values/utils/countries';
import classNames from 'classnames/bind';
import Select from '../../Select/Select';

import formStyle from '../../../assets/styles/form.scss';
import inputStyle from '../../../assets/styles/input.scss';

import type { Option } from '../../Select/Select';
import type { Country } from '../../../entities';
import type { LocalValuesState } from '../../../modules/local-values/duck';

type Props = {
  +isPutting?: boolean;
  +isPosting?: boolean;
  +handleChange: (e: SyntheticInputEvent<HTMLInputElement>) => void;
  +handleFocus: (e: Event) => void;
  +handleChangeCountry: (option: Option) => void;
  +country: {
    value: Country;
    errors?: string[];
  };
  +firstName: {
    value: string;
    errors?: string[];
  };
  +idNumber: {
    value: string;
    errors?: string[];
  };
  +lastName: {
    value: string;
    errors?: string[];
  };
  +name: {
    value: string;
    errors?: string[];
  };
  +localValues?: LocalValuesState;
};

const messages = defineMessages({
  country: {
    label: {
      id: 'address.country.label',
    },
    placeholder: {
      id: 'address.country.placeholder',
    },
  },
  firstName: {
    label: {
      id: 'address.firstName.label',
    },
    placeholder: {
      id: 'address.firstName.placeholder',
    },
  },
  idNumber: {
    label: {
      id: 'address.idNumber.label',
    },
    labelCO: {
      id: 'address.idNumber.label.CO',
    },
    placeholder: {
      id: 'address.idNumber.placeholder',
    },
    placeholderCO: {
      id: 'address.idNumber.placeholder.CO',
    },
  },
  lastName: {
    label: {
      id: 'address.lastName.label',
    },
    placeholder: {
      id: 'address.lastName.placeholder',
    },
  },
  name: {
    label: {
      id: 'preferences.savedAddresses.name.label',
    },
    labelCO: {
      id: 'preferences.savedAddresses.name.label.CO',
    },
    placeholder: {
      id: 'preferences.savedAddresses.name.placeholder',
    },
  },
});

export default class Top extends Component<Props> {

  render() {
    const {
      isPutting, isPosting, handleChange, handleFocus, handleChangeCountry,
      country, firstName, idNumber, lastName, name, localValues,
    } = this.props;
    const options: Option[] = (localValues && localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      value: country.key,
      label: !localValues ? '' :
        getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
    }));
    const countryError = country.errors && FormValidator.buildErrorLabel(country.errors, {
      required: 'address.country.error.required',
    });
    const firstNameError = firstName.errors && FormValidator.buildErrorLabel(firstName.errors, {
      required: 'address.firstName.error.required',
    });
    const idNumberError = idNumber.errors && FormValidator.buildErrorLabel(idNumber.errors, {
      required: 'address.idNumber.error.required',
    });
    const lastNameError = lastName.errors && FormValidator.buildErrorLabel(lastName.errors, {
      required: 'address.lastName.error.required',
    });
    const nameError = name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'preferences.savedAddresses.name.error.required',
    });
    const countryIsUS = country.value === 'US';
    return (
      <form className={formStyle.formWrap}>
        <section
          className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsiveMedium)}>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={messages.country.label.id}>{
              txt => (<label className={formStyle.label}>{txt}:</label>)
            }</FormattedMessage>
            <Select options={options}
                    className={classNames({
                      'light-blue': country.value,
                      'red': country.errors,
                    })}
                    value={country.value}
                    disabled={isPosting || isPutting}
                    searchable={true}
                    placeholder={this.context.intl.formatMessage(messages.country.placeholder)}
                    handleChange={handleChangeCountry}/>
            {countryError}
          </article>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={country.value === 'CO' ? messages.name.labelCO.id : messages.name.label.id}>{
              txt => (<label className={formStyle.label}>{txt}:</label>)
            }</FormattedMessage>
            <input disabled={isPosting || isPutting}
                   className={classNames(inputStyle.input, { [inputStyle.red]: name.errors && name.errors.length },
                     { [inputStyle.lightBlue]: !!name.value })}
                   value={name.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="name"
                   placeholder={this.context.intl.formatMessage(messages.name.placeholder)}/>
            {nameError}
          </article>
        </section>
        <section
          className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsiveMedium)}>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={messages.firstName.label.id}>{
              txt => (<label className={formStyle.label}>{txt}:</label>)
            }</FormattedMessage>
            <input disabled={isPosting}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: firstName.errors && firstName.errors.length },
                     { [inputStyle.lightBlue]: !!firstName.value })}
                   value={firstName.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="firstName"
                   placeholder={this.context.intl.formatMessage(messages.firstName.placeholder)}/>
            {firstNameError}
          </article>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={messages.lastName.label.id}>{
              txt => (<label className={formStyle.label}>{txt}:</label>)
            }</FormattedMessage>
            <input disabled={isPosting}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: lastName.errors && lastName.errors.length },
                     { [inputStyle.lightBlue]: !!lastName.value })}
                   value={lastName.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="lastName"
                   placeholder={this.context.intl.formatMessage(messages.lastName.placeholder)}/>
            {lastNameError}
          </article>
        </section>
        {!countryIsUS &&
        <section
          className={classNames(formStyle.row, formStyle.inputRow, formStyle.dualWrap, formStyle.responsiveMedium)}>
          <article className={classNames(formStyle.col)}>
            <FormattedMessage id={country === 'CO' ?
              messages.idNumber.labelCO.id : messages.idNumber.label.id}>{
              txt => (<label className={formStyle.label}>{txt}:</label>)
            }</FormattedMessage>
            <input disabled={isPosting}
                   className={classNames(inputStyle.input,
                     { [inputStyle.red]: idNumber.errors && idNumber.errors.length },
                     { [inputStyle.lightBlue]: !!idNumber.value })}
                   value={idNumber.value}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   name="idNumber"
                   placeholder={country === 'CO' ?
                     this.context.intl.formatMessage(messages.idNumber.placeholderCO) :
                     this.context.intl.formatMessage(messages.idNumber.placeholder)}
                   type="tel"/>
            {idNumberError}
          </article>
        </section>}
      </form>
    )
  }
}

Top.propTypes = {
  isPutting: PropTypes.bool,
  isPosting: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleFocus: PropTypes.func.isRequired,
  handleChangeCountry: PropTypes.func.isRequired,
  country: PropTypes.object.isRequired,
  firstName: PropTypes.object.isRequired,
  idNumber: PropTypes.object.isRequired,
  lastName: PropTypes.object.isRequired,
  name: PropTypes.object.isRequired,
  localValues: PropTypes.object,
};

Top.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/elements/AddressForm/ModalType/Top.jsx